import { lazy, Suspense, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import { Toaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import Landing from '@/pages/Landing';
import FAQPage from '@/pages/FAQPage';
import Plans from '@/pages/Plans';
import ShareLink from '@/pages/ShareLink';
import Link from '@/pages/Link';
import LinkCode from '@/pages/LinkCode';
import Auth from '@/pages/Auth';
import PreferencesEntry from '@/pages/PreferencesEntry';
import SignIn from '@/pages/SignIn';
import Success from '@/pages/Success';
import Setup from '@/pages/Setup';
import Billing from '@/pages/Billing';
import PaymentReturn from '@/pages/PaymentReturn';
import Pay from '@/pages/Pay';
import Account from '@/pages/Account';
import Relink from '@/pages/Relink';
import Referrals from '@/pages/Referrals';
import DevOnboarding from '@/pages/DevOnboarding';
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import { capturePostHogException, isMcpAuthorizationSurface, sanitizeTelemetryRoute, stopSensitivePostHogRecording } from "@/lib/posthog";
import { SHORT_PREFERENCES_PATH } from "@/lib/preferences-route";
import { syncSupabaseSessionFromUrl } from "@/lib/supabase-session";
import { I18nProvider } from "@/lib/i18n";
import { DEFAULT_LANGUAGE_CODE, resolveRouteUiLanguageCode } from "@/lib/languages";
import RouteLoadingShell from "@/components/shared/RouteLoadingShell";
import { useRouteScrollRestoration } from "@/hooks/use-route-scroll-restoration";

const Changelog = lazy(() => import("@/pages/Changelog"));
const AgentConnect = lazy(() => import("@/pages/AgentConnect"));
const McpAuthorize = lazy(() => import("@/pages/McpAuthorize"));
const McpUpstreamAuthorize = lazy(() => import("@/pages/McpUpstreamAuthorize"));
const McpSetup = lazy(() => import("@/pages/McpSetup"));
const McpKeyDelivery = lazy(() => import("@/pages/McpKeyDelivery"));
const McpMediaDownload = lazy(() => import("@/pages/McpMediaDownload"));
const PublicInfo = lazy(() => import("@/pages/PublicInfo"));

const ChangelogRoute = () => (
  <Suspense fallback={<RouteLoadingShell />}>
    <Changelog />
  </Suspense>
);

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/l/:locale" element={<Landing />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/l/:locale/faq" element={<FAQPage />} />
      {["support", "privacy", "terms"].flatMap((page) => [
        <Route key={page} path={`/${page}`} element={<Suspense fallback={<RouteLoadingShell />}><PublicInfo page={page} /></Suspense>} />,
        <Route key={`localized-${page}`} path={`/l/:locale/${page}`} element={<Suspense fallback={<RouteLoadingShell />}><PublicInfo page={page} /></Suspense>} />,
      ])}
      <Route path="/plans" element={<Plans />} />
      <Route path="/l/:locale/plans" element={<Plans />} />
      <Route path="/changelog" element={<ChangelogRoute />} />
      <Route path="/changelog/:slug" element={<ChangelogRoute />} />
      <Route path="/l/:locale/changelog" element={<ChangelogRoute />} />
      <Route path="/l/:locale/changelog/:slug" element={<ChangelogRoute />} />
      <Route path="/m/:code" element={<ShareLink />} />
      <Route path={SHORT_PREFERENCES_PATH} element={<PreferencesEntry />} />
      <Route path="/link" element={<Link />} />
      <Route path="/link-code" element={<LinkCode />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/success" element={<Success />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/payments/return" element={<PaymentReturn />} />
      <Route path="/pay" element={<Pay />} />
      <Route path="/account" element={<Account />} />
      <Route path="/agent" element={<Suspense fallback={<RouteLoadingShell />}><AgentConnect /></Suspense>} />
      <Route path="/agent/authorize" element={<Suspense fallback={<RouteLoadingShell />}><McpAuthorize /></Suspense>} />
      <Route path="/agent/upstream-authorize" element={<Suspense fallback={<RouteLoadingShell />}><McpUpstreamAuthorize /></Suspense>} />
      <Route path="/agent/setup" element={<Suspense fallback={<RouteLoadingShell />}><McpSetup /></Suspense>} />
      <Route path="/agent/keys/:issuanceId" element={<Suspense fallback={<RouteLoadingShell />}><McpKeyDelivery /></Suspense>} />
      <Route path="/agent/media/:locator" element={<Suspense fallback={<RouteLoadingShell />}><McpMediaDownload /></Suspense>} />
      <Route path="/relink" element={<Relink />} />
      <Route path="/referrals" element={<Referrals />} />
      <Route path="/relink" element={<Relink />} />
      {ONBOARDING_DESIGN_MODE ? <Route path="/dev/onboarding" element={<DevOnboarding />} /> : null}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

const LocalizedAppShell = () => {
  const location = useLocation();
  useEffect(() => { stopSensitivePostHogRecording(location); }, [location]);
  const isLandingPage = location.pathname === "/"
    || /^\/l\/[^/]+\/?$/.test(location.pathname);
  useRouteScrollRestoration({ resetOnReload: isLandingPage });
  const isEnglishDefaultPublicPage = location.pathname === "/"
    || location.pathname === "/plans"
    || location.pathname === "/faq"
    || ["/support", "/privacy", "/terms"].includes(location.pathname)
    || location.pathname === "/changelog"
    || location.pathname.startsWith("/changelog/");
  const routeLocale = resolveRouteUiLanguageCode(
    location.pathname,
    DEFAULT_LANGUAGE_CODE,
  );
  const providerKey = isEnglishDefaultPublicPage
    ? `english-default-public:${location.pathname}`
    : (routeLocale ? `route-locale:${routeLocale}` : "persisted-locale");

  return (
    <I18nProvider
      key={providerKey}
      initialLocale={isEnglishDefaultPublicPage ? DEFAULT_LANGUAGE_CODE : routeLocale}
      persistLocale={!isEnglishDefaultPublicPage}
    >
      <SupabaseAuthCallbackBootstrap />
      <AppRoutes />
      <VercelObservability />
      <Toaster />
    </I18nProvider>
  );
};

const VercelObservability = () => {
  const location = useLocation();
  const route = sanitizeTelemetryRoute(location);

  if (ONBOARDING_DESIGN_MODE || location.pathname === "/pay" || isMcpAuthorizationSurface(location)) {
    return null;
  }

  return (
    <>
      <Analytics route={route} />
      <SpeedInsights route={route} />
    </>
  );
};

const SupabaseAuthCallbackBootstrap = () => {
  useEffect(() => {
    if (ONBOARDING_DESIGN_MODE) {
      return;
    }
    void syncSupabaseSessionFromUrl().catch((error) => {
      console.warn("Failed to sync Supabase auth callback", error);
      capturePostHogException(error, {
        error_context: "supabase_auth_callback_sync_failed",
      });
    });
  }, []);

  return null;
};

function App() {

  return (
    <QueryClientProvider client={queryClientInstance}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <LocalizedAppShell />
      </Router>
    </QueryClientProvider>
  )
}

export default App
